<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { FileText } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import UploadedFileActionMenu from '@/components/files/UploadedFileActionMenu.vue';
import { formatShortDate } from '~/lib/formatters';
import type { UploadedFile } from '~/types/models';

const props = withDefaults(defineProps<{
    files: UploadedFile[];
    loading?: boolean;
    emptyText?: string;
    ariaLabel?: string;
    batchSize?: number;
    showScope?: boolean;
    layout?: 'list' | 'grid';
    showActions?: boolean;
    canMutate?: boolean;
    projectOptions?: Array<{ value: string, label: string }>;
    busyFileId?: string;
    selectable?: boolean;
    selectedFileIds?: string[];
}>(), {
    loading: false,
    emptyText: 'No files uploaded yet.',
    ariaLabel: 'Uploaded files',
    batchSize: 20,
    showScope: false,
    layout: 'list',
    showActions: false,
    canMutate: false,
    projectOptions: () => [],
    busyFileId: '',
    selectable: false,
    selectedFileIds: () => [],
});

const emit = defineEmits<{
    (event: 'add-to-project', payload: { file: UploadedFile, projectId: string }): void;
    (event: 'set-cover', file: UploadedFile): void;
    (event: 'toggle-portal-visibility', file: UploadedFile): void;
    (event: 'delete', file: UploadedFile): void;
    (event: 'toggle-select', fileId: string): void;
}>();

const visibleCount = ref(props.batchSize);
const visibleFiles = computed(() => props.files.slice(0, visibleCount.value));
const hasMore = computed(() => visibleFiles.value.length < props.files.length);

watch(() => props.files.length, (length) => {
    if (length < visibleCount.value) {
        visibleCount.value = Math.max(props.batchSize, length);
    }
});

function revealNextBatch() {
    visibleCount.value = Math.min(props.files.length, visibleCount.value + props.batchSize);
}

function handleScroll(event: Event) {
    if (!hasMore.value) return;
    const target = event.currentTarget as HTMLElement;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining < 160) revealNextBatch();
}

function fileMeta(file: UploadedFile) {
    const date = formatShortDate(file.createdAt, 'No date yet');
    if (!props.showScope) return date;

    const scopeLabel = file.entityType === 'project'
        ? props.projectOptions.find((project) => project.value === file.entityId)?.label || 'Project'
        : file.entityType === 'client'
            ? 'Client workspace'
            : file.entityType.replaceAll('_', ' ');

    return `${scopeLabel} • ${date}`;
}
</script>

<template>
    <div
        class="max-h-[28rem] overflow-y-auto overscroll-contain px-2 pb-1"
        :aria-label="ariaLabel"
        tabindex="0"
        @scroll.passive="handleScroll"
    >
        <div v-if="loading && files.length === 0" class="space-y-2" aria-label="Loading files">
            <Skeleton v-for="index in 4" :key="index" class="h-14 w-full rounded-xl" />
        </div>

        <div
            v-else-if="visibleFiles.length"
            :class="layout === 'grid' ? 'grid gap-3 sm:grid-cols-2' : 'divide-y divide-border/60'"
        >
            <div
                v-for="file in visibleFiles"
                :key="file.id"
                class="group relative flex min-w-0 gap-3 transition-colors hover:bg-muted/35"
                :class="[
                    layout === 'grid' ? 'min-h-36 flex-col items-center justify-center rounded-xl border border-border/60 bg-background/70 p-4 text-center' : 'items-start rounded-lg px-3 py-3',
                    selectedFileIds.includes(file.id) ? 'bg-primary/10 ring-1 ring-inset ring-primary/40' : '',
                ]"
            >
                <Checkbox
                    v-if="selectable"
                    :model-value="selectedFileIds.includes(file.id)"
                    :class="layout === 'grid' ? 'absolute left-3 top-3' : 'mt-2'"
                    :aria-label="`Select ${file.fileName}`"
                    @click.stop
                    @update:model-value="emit('toggle-select', file.id)"
                />
                <a
                    :href="file.fileURL"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex min-w-0 flex-1 gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    :class="layout === 'grid' ? 'w-full flex-col items-center justify-center pt-4 text-center' : 'items-start'"
                >
                    <div
                        class="flex shrink-0 items-center justify-center rounded-xl bg-muted/60"
                        :class="layout === 'grid' ? 'size-12' : 'mt-0.5 size-8'"
                    >
                        <FileText :class="layout === 'grid' ? 'size-5' : 'size-3.5'" class="text-sky-600 dark:text-sky-300" />
                    </div>
                    <div class="min-w-0 flex-1">
                        <p class="truncate text-sm font-medium text-foreground">{{ file.fileName }}</p>
                        <slot name="meta" :file="file">
                            <p class="mt-1 truncate text-xs text-muted-foreground">{{ fileMeta(file) }}</p>
                        </slot>
                    </div>
                </a>

                <UploadedFileActionMenu
                    v-if="showActions"
                    :class="layout === 'grid' ? 'absolute right-2 top-2' : ''"
                    :file="file"
                    :project-options="projectOptions"
                    :busy="busyFileId === file.id"
                    :allow-add-to-project="canMutate"
                    :allow-set-cover="canMutate"
                    :allow-toggle-portal-visibility="canMutate"
                    :allow-delete="canMutate"
                    @add-to-project="emit('add-to-project', $event)"
                    @set-cover="emit('set-cover', $event)"
                    @toggle-portal-visibility="emit('toggle-portal-visibility', $event)"
                    @delete="emit('delete', $event)"
                />
            </div>
        </div>

        <p v-else class="py-6 text-sm text-muted-foreground">{{ emptyText }}</p>

        <div v-if="hasMore" class="sticky bottom-0 flex justify-center bg-background/95 py-2 backdrop-blur">
            <Button type="button" variant="ghost" size="sm" @click="revealNextBatch">
                Load more files
            </Button>
        </div>
    </div>
</template>
